// Import the functions you need from the SDKs you need
import { initializeApp } from 'firebase/app';
import { getAuth, sendEmailVerification, reload, sendPasswordResetEmail } from 'firebase/auth';

// email validation regex (exported for use in auth forms)
export const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

// TODO: Add SDKs for Firebase products that you want to use
// https://firebase.google.com/docs/web/setup#available-libraries

// Check for specific error types and provide meaningful messages
export function handleFirebaseError(error) {
  const code = error?.code;
  const message = error?.message || '';

  // In frontend, we might not have a global translateRaw yet, but we can return keys 
  // if the caller uses a translation hook, OR we can return English friendly strings if not.
  // However, looking at the codebase, handleFirebaseError is expected to return strings.

  // 1. Check by standard Firebase error codes
  if (code === 'auth/invalid-credential') return 'Invalid email or password. Please check your credentials.';
  if (code === 'auth/user-disabled') return 'This account has been disabled. Please contact support.';
  if (code === 'auth/user-not-found') return 'No account found with this email.';
  if (code === 'auth/wrong-password') return 'Incorrect password. Please try again.';
  if (code === 'auth/too-many-requests') return 'Too many failed attempts. Please try again later.';
  if (code === 'auth/invalid-email') return 'The email address is not valid.';
  if (code === 'auth/email-already-in-use') return 'An account with this email already exists. Please sign in instead.';

  // 2. Fallback to message inspection
  if (message.includes('auth/invalid-credential')) return 'Invalid email or password. Please check your credentials.';
  if (message.includes('network')) return 'Network error. Please check your connection.';
  if (message.includes('required module')) return 'App configuration error. Please contact support.';
  if (message.includes('Email not confirmed')) return 'Please check your email and click the confirmation link.';
  if (message.includes('User already registered') || message.includes('auth/email-already-in-use')) {
    return 'An account with this email already exists. Please sign in instead.';
  }
  if (message.includes('Password should be at least 6 characters')) {
    return 'Password must be at least 6 characters long.';
  }

  return message || 'An unexpected error occurred';
}
// Your web app's Firebase configuration
// For Firebase JS SDK v7.20.0 and later, measurementId is optional
const firebaseConfig = {
  apiKey: process.env.REACT_APP_FIREBASE_API_KEY,
  authDomain: process.env.REACT_APP_FIREBASE_AUTH_DOMAIN,
  projectId: process.env.REACT_APP_FIREBASE_PROJECT_ID,
  storageBucket: process.env.REACT_APP_FIREBASE_STORAGE_BUCKET,
  messagingSenderId: process.env.REACT_APP_FIREBASE_MESSAGING_SENDER_ID,
  appId: process.env.REACT_APP_FIREBASE_APP_ID,
  measurementId: process.env.REACT_APP_FIREBASE_MEASUREMENT_ID,
};

export function validateUserInput(email, password) {
  if (!email || !EMAIL_REGEX.test(email)) {
    return { valid: false, message: 'Please enter a valid email address.' };
  }
  if (!password || password.length < 6) {
    return {
      valid: false,
      message: 'Password must be at least 6 characters long.',
    };
  }
  return { valid: true };
}
export async function signInWithEmail(email, password) {
  const { valid, message } = validateUserInput(email, password);
  if (!valid) {
    throw new Error(message);
  }
  const auth = getAuth();
  try {
    const userCredential = await auth.signInWithEmailAndPassword(
      email,
      password
    );
    return userCredential.user;
  } catch (error) {
    throw new Error(handleFirebaseError(error));
  }
}

export async function signUpWithEmail(email, password) {
  const { valid, message } = validateUserInput(email, password);
  if (!valid) {
    throw new Error(message);
  }
  const auth = getAuth();
  try {
    const userCredential = await auth.createUserWithEmailAndPassword(
      email,
      password
    );
    return userCredential.user;
  } catch (error) {
    throw new Error(handleFirebaseError(error));
  }
}

// Send verification email
export async function sendVerificationEmailToUser(user) {
  try {
    await sendEmailVerification(user);
    return 'Verification email sent. Please check your inbox.';
  } catch (error) {
    throw new Error(handleFirebaseError(error));
  }
}

// Resend verification email
export async function resendVerificationEmail() {
  try {
    const user = auth.currentUser;
    if (!user) throw new Error('No user is currently signed in.');
    await sendVerificationEmailToUser(user);
    return 'Verification email sent. Please check your inbox.';
  } catch (error) {
    throw new Error(handleFirebaseError(error));
  }
}

// Optional helper: check if email is verified
export async function checkEmailVerified(user) {
  try {
    await reload(user); // refresh user data
    return user.emailVerified;
  } catch (error) {
    throw new Error(handleFirebaseError(error));
  }
}

export async function sendPasswordReset(email) {
  if (!email || !EMAIL_REGEX.test(email)) {
    throw new Error('Please enter a valid email address.');
  }

  const auth = getAuth();
  try {
    await sendPasswordResetEmail(auth, email, {
      url: `${process.env.REACT_APP_API_URL}/auth/sign-in`,
    });
    return 'Password reset email sent. Please check your inbox.';
  } catch (error) {
    throw new Error(handleFirebaseError(error));
  }
}

// Initialize Firebase
const app = initializeApp(firebaseConfig);

export const auth = getAuth(app);
